import { Textarea } from "@/shared/components/ui/textarea";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import t from "@/lang/lang";
import { formatDateTimeLocal } from "@/shared/lib/utilities/date";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { PostalAddressCardWithMutation } from "@/services/backend/addresses/components/postal-address-card";
import { fieldErrorsForPrefix } from "@/shared/lib/errors/validation-error";
import { ServiceAddressCardWithMutation } from "@/services/backend/addresses/components/service-address-card";
import { useErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import {
  useQpmRequirementUpdatePostalAddressMutation,
  useQpmRequirementUpdateServiceAddressMutation,
} from "@/services/backend/qpm/requirements/api/requirement-api";
import { ChangeEvent } from "react";
import {
  useRequirementData,
  useRequirementEdit,
} from "@/services/backend/qpm/requirements/hooks/requirement-context";

export function InfoRoute() {
  const { requirement, isLoading, error } = useRequirementData();

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <div className="mt-2">
        <Skeleton className="mb-2 h-24 w-full" />
        <Skeleton className="mb-2 h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (!requirement) {
    return <div>{t("Requirement data not available")}</div>;
  }

  return <DetailsComponent />;
}

function DetailsComponent() {
  const { putRequest, updateRequirement } = useRequirementEdit();
  const { notReadyError, resetNotReadyErrorField } = useErrDocumentNotReady();

  const { requirement } = putRequest;

  if (!requirement) {
    return <div>{t("Requirement data not available")}</div>;
  }

  const handleSubjectChange = (e: ChangeEvent<HTMLInputElement>) => {
    updateRequirement({
      subject: e.target.value,
    });
  };

  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    updateRequirement({
      description: e.target.value,
    });
  };

  return (
    <div className="grid gap-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="subject">{t("Thema")}</Label>
          <Input
            id="subject"
            type="text"
            className="w-full"
            value={requirement.subject || ""}
            onChange={handleSubjectChange}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="number">{t("Nummer")}</Label>
          <div className="border-input text-muted-foreground flex h-10 items-center rounded-md border px-3 py-2 text-sm">
            <DocumentNumberById documentId={requirement.id} />
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="description">{t("Beschreibung")}</Label>
          <Textarea
            id="description"
            value={requirement.description || ""}
            className="min-h-10"
            onChange={handleDescriptionChange}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="date">{t("Erstellt am")}</Label>
          <Input
            id="date"
            type="datetime-local"
            className="w-full"
            value={formatDateTimeLocal(requirement.createdAt)}
            disabled
          />
        </div>
      </div>
      <div className="space-y-4">
        <PostalAddressCardWithMutation
          id={requirement.id}
          address={requirement.postalAddress}
          mutation={useQpmRequirementUpdatePostalAddressMutation}
          fieldErrors={fieldErrorsForPrefix(notReadyError, "postalAddress.")}
          resetFieldError={(field) =>
            resetNotReadyErrorField(`postalAddress.${field}`)
          }
        />
        <ServiceAddressCardWithMutation
          id={requirement.id}
          address={requirement.serviceAddress}
          mutation={useQpmRequirementUpdateServiceAddressMutation}
          fieldErrors={fieldErrorsForPrefix(notReadyError, "serviceAddress.")}
          resetFieldError={(field) =>
            resetNotReadyErrorField(`serviceAddress.${field}`)
          }
        />
      </div>
    </div>
  );
}
